import "./FailureResponseDialog.scss";

import { FunctionComponent, useEffect, useState } from "react";
import Dialog from "../Dialog/Dialog";
import ButtonField from "../Button/ButtonField";
import { IAPIResponse } from "../../Redux/interfaces/IAPIResponse";
import { SvgIcon } from "@mui/material";
import { ReactComponent as Success } from "../../Assets/Images/success.svg";
import { ReactComponent as Failure } from "../../Assets/Images/failure.svg";
import { useNavigate } from "react-router-dom";

interface FailureResponseDialogProps {
  response?: IAPIResponse<any>;
  parentDialog?: React.Dispatch<React.SetStateAction<boolean>>;
}

const FailureResponseDialog: FunctionComponent<FailureResponseDialogProps> = (
  props
) => {
  const { response, parentDialog } = props;
  const [open, setOpen] = useState<boolean>(false);
  const nav = useNavigate();

const handleClose = () => {
  setOpen(false);
  response?.status && parentDialog && parentDialog(false);
};

useEffect(() => {
  if (!response || Object.keys(response).length === 0) {
    return;
  }

  // Check if response is successful (should not show failure dialog)
  const isSuccess = response.responseMessage === 'success' || 
                    response.responseCode === 200 || 
                    (response.response?.status === true && (response.responseCode === undefined || (response.responseCode >= 200 && response.responseCode < 300)));

  // If it's a success response, don't show the failure dialog
  if (isSuccess) {
    setOpen(false);
    return;
  }

  // Check if response is an error
  const isError = response.responseMessage === 'failed' ||
                  (response.responseCode !== undefined && (response.responseCode < 200 || response.responseCode >= 400)) ||
                  response.status_code === 400 ||
                  response.status_code === 401 ||
                  response.status_code === 409 ||
                  (response.response?.error !== undefined);

  // Only show failure dialog for actual errors
  if (isError) {
    // Don't navigate on 400 errors for forgot password - let user see the error
    // Only navigate on 400 if it's a different context
    if (response.status_code === 400 || response.responseCode === 400) {
      // Only navigate if it's not a forgot password error
      // For forgot password, we want to show the error
    }
    setOpen(true);
  } else {
    setOpen(false);
  }
  
  // Navigate on success (only for specific flows like login)
  if (isSuccess && response.status && response.responseCode === 200) {
    // Only navigate for specific flows, not for unlock
    // nav('/login');
  }
}, [response, nav]);

// Extract error message from various possible locations
const getErrorMessage = () => {
  if (!response) return 'An error occurred';
  
  // Check response.response.error first (most common for API errors)
  if (response.response?.error) {
    const error = response.response.error as any; // Type assertion to handle string, object, or array
    // Handle array of errors (validation errors)
    if (Array.isArray(error)) {
      return error.map((err: any) => 
        typeof err === 'string' ? err : err?.msg || err?.message || JSON.stringify(err)
      ).join(', ');
    }
    // Handle string error
    if (typeof error === 'string') {
      return error;
    }
    // Handle object error
    if (typeof error === 'object' && error !== null) {
      return error?.msg || error?.message || JSON.stringify(error);
    }
  }
  
  // Check if error is directly in response.error (for some error formats)
  // Note: error might exist at top level in some cases, use type assertion
  const topLevelError = (response as any).error;
  if (topLevelError) {
    if (Array.isArray(topLevelError)) {
      return topLevelError.map((err: any) => 
        typeof err === 'string' ? err : err?.msg || err?.message || JSON.stringify(err)
      ).join(', ');
    }
    return typeof topLevelError === 'string' ? topLevelError : JSON.stringify(topLevelError);
  }
  
  // Check response.response.message
  if (response.response?.message) {
    return response.response.message;
  }
  
  // Check response.message
  if (response.message) {
    return response.message;
  }
  
  // Check response.response.data.error
  const dataError = response.response?.data as any;
  if (dataError?.error) {
    if (Array.isArray(dataError.error)) {
      return dataError.error.map((err: any) => 
        typeof err === 'string' ? err : err?.msg || err?.message || JSON.stringify(err)
      ).join(', ');
    }
    return typeof dataError.error === 'string' 
      ? dataError.error 
      : JSON.stringify(dataError.error);
  }
  
  // Check response.response.data.response
  if (response.response?.data?.response) {
    return response.response.data.response;
  }
  
  // Check if response.response is a string
  if (typeof response.response === 'string') {
    return response.response;
  }
  
  // Default error message
  return 'An error occurred. Please try again.';
};

return (
  <Dialog
    open={open}
    dialogContentClassName="response_content"
    Dialog_Content={
      <>
        {getErrorMessage()}
        <SvgIcon
          component={response?.status && response?.responseCode === 200 ? Success : Failure}
          inheritViewBox
          className="response_icon"
        />
      </>
    }
    Dialog_Actions={
      <ButtonField
        className="response_button"
        onClick={() => handleClose()}>
        OK
      </ButtonField>
    }
  />
);
};

export default FailureResponseDialog;
